import { generateSheetTicks } from '@ezchops/musicsheet';
import { ConstantTempoTiming } from '@ezchops/timing';
import { chain, maxBy, minBy, omit } from 'lodash';
import { Evaluator } from './Evaluator';
export class SheetEvaluator extends Evaluator {
    _ticks;
    _evaluation;
    constructor(practice, tickNote, options = {}) {
        super(ConstantTempoTiming.sheetTiming(practice.sheet, practice.tempo, tickNote), practice, options);
        this._ticks = this.emptyTicks;
        this._evaluation = this.emptyEvaluation;
    }
    evaluate = (from, to) => {
        return this.evaluation.slice(from, to);
    };
    get sheet() {
        return this.practice.sheet;
    }
    get evaluation() {
        return this._evaluation;
    }
    get ticks() {
        return this._ticks;
    }
    isMissed(absTick, expected) {
        return this.evaluation[absTick].evaluated.some((h) => h.expected == expected && h.hit == null);
    }
    reset = () => {
        super._reset();
        this._ticks = this.emptyTicks;
        this._evaluation = this.emptyEvaluation;
    };
    push = (...hits) => {
        super._push(...hits);
        this.mapToTicks(hits);
    };
    sheetPosition = (position) => {
        const tick = this.ticks[Math.floor(position.absTick)];
        return tick == null ? null : omit(tick, ['notes', 'timestamp', 'hits']);
    };
    mapToTicks = (hits) => {
        const snapped = hits.map(this.snapHit);
        chain(snapped)
            .groupBy('tick')
            .forEach((snapped, ts) => {
            const tick = parseInt(ts);
            if (0 <= tick && tick < this._ticks.length) {
                const mappedTick = this._ticks[tick];
                mappedTick.hits.push(...snapped);
                this.evaluateTick(tick);
            }
            else {
                console.warn(`Tick ${tick} is out of bounds`);
            }
        })
            .value();
    };
    evaluateTick = (tick) => {
        const mappedTick = this._ticks[tick];
        if (mappedTick == null) {
            return;
        }
        const empty = {
            ...omit(mappedTick, ['hits', 'notes']),
            evaluated: []
        };
        this._evaluation[tick] = assignHitsToExpectedNotes(empty, mappedTick);
    };
    get emptyTicks() {
        return [...generateSheetTicks(this.practice.sheet, this.timing.tickNote)].map((tickNotes, tick) => ({
            ...tickNotes,
            timestamp: this.timing.tickTimestamp(tick),
            hits: []
        }));
    }
    get emptyEvaluation() {
        return this.ticks.map((tick) => ({
            ...omit(tick, ['hits', 'notes']), evaluated: tick.notes.filter(n => n.pitch != null)
                .map((expected) => ({ expected }))
        }));
    }
    label = ({ expected, hit, lag }) => {
        if (expected == null && hit == null) {
            throw new Error('No hit nor expected');
        }
        if (expected == null) {
            return 'unexpected';
        }
        if (hit == null) {
            if (expected.pitch == null) {
                return 'skipped';
            }
            else {
                return 'missed';
            }
        }
        if (lag == null) {
            throw new Error('No lag');
        }
        const threshold = 1 / this.options.resolution;
        if (lag < -threshold) {
            return 'early';
        }
        if (lag > threshold) {
            return 'late';
        }
        return 'accurate';
    };
    score = ({ expected, hit, lag }) => {
        if (expected == null && hit == null) {
            throw new Error('No hit nor expected');
        }
        if (expected == null) {
            return -1;
        }
        if (hit == null) {
            return 0;
        }
        if (lag == null) {
            throw new Error('No lag');
        }
        return Math.exp(-0.5 * (lag / this.sigma) ** 2);
        //return 1 - (Math.abs(lag) / threshold)
    };
    maxScore = ({ expected, hit, lag }) => {
        if (expected == null && hit == null) {
            throw new Error('No hit nor expected');
        }
        if (expected == null) {
            return 0;
        }
        return 1;
    };
}
function assignHitsToExpectedNotes(assigned, mapped) {
    const { notes, hits } = mapped;
    const { evaluated } = assigned;
    // No more notes, the rest of hits is unexpected
    if (notes.length == 0) {
        const unexpected = hits.map(({ hit, note, lag }) => ({ hit, note, lag }));
        return { ...assigned, evaluated: [...evaluated, ...unexpected] };
    }
    // No more hits, the rest of notes is missed
    if (hits.length == 0) {
        const missing = notes.filter(note => note.pitch != null).map(note => ({ expected: note }));
        return { ...assigned, evaluated: [...evaluated, ...missing] };
    }
    const expected = notes.find(x => x.pitch != null) ?? notes[0];
    const matching = hits.filter(h => expected.pitch == null || h.note.pitch == expected.pitch);
    if (matching.length > 0) {
        const { hit, lag, note } = selectPrimaryHit(matching);
        const remainingHits = hits.filter(h => h.hit != hit);
        const hasMoreImproHits = (expected.pitch == null) && (matching.length > 1);
        const remainingNotes = mapped.notes.filter(n => hasMoreImproHits || n != expected);
        return assignHitsToExpectedNotes({ ...assigned, evaluated: [...evaluated, { expected, hit, note, lag }] }, { ...mapped, hits: remainingHits, notes: remainingNotes });
    }
    else if (expected.pitch != null) {
        const remainingNotes = mapped.notes.filter(n => n != expected);
        return assignHitsToExpectedNotes({ ...assigned, evaluated: [...evaluated, { expected }] }, { ...mapped, notes: remainingNotes });
    }
    else {
        const remainingNotes = mapped.notes.filter(n => n != expected);
        return assignHitsToExpectedNotes(assigned, { ...mapped, notes: remainingNotes });
    }
}
function selectPrimaryHit(matchingHits) {
    const strongest = maxBy(matchingHits, h => h.hit.value);
    const closest = minBy(matchingHits, h => Math.abs(h.lag));
    if (strongest == null || closest == null) {
        throw new Error('Cannot select primary, no hits');
    }
    if (strongest == closest) {
        return strongest;
    }
    if (strongest.hit.value > closest.hit.value * 2) {
        return strongest;
    }
    return closest;
}
//# sourceMappingURL=SheetEvaluator.js.map